/**
 * The three kinds of time, as one type (#11055).
 *
 * The app has re-derived "is this a calendar date or an instant?" from the
 * shape of a string in six independent places, with two mutually incompatible
 * answers -- half forcing UTC, half reading the viewer's local zone, so the
 * same value renders as two different days depending on which component drew
 * it. #2797, #3839, #5228, #7818, #8097, #8191, #8334, #8762, #8987, #9205,
 * #9930 and #11055 are that defect fixed twelve times in twelve components.
 *
 * This module is the one place that decides. The rules it encodes:
 *
 * - **A calendar date is invariant.** A day carries no zone, so it renders
 *   identically for every viewer on earth. It is held as a `Temporal.PlainDate`
 *   and never reaches the `Date` constructor -- `new Date("2026-02-28")` is
 *   midnight UTC and renders as the 27th everywhere in the Americas, which is
 *   precisely the bug.
 * - **An instant is a point on the timeline.** Its calendar day genuinely
 *   depends on who is reading, so it is always rendered *into a named zone*
 *   that the caller passes explicitly. Nothing here defaults that zone: the
 *   caller knows whether the right answer is the viewer's zone (display,
 *   filters) or the value's own (an interaction shown in the zone it happened
 *   in), and a default would silently pick one.
 * - **`granularity` is a tag, not a binary.** Consumers match on it with an
 *   exhaustive check, so adding `month` or `quarter` later is a compile error
 *   at every site that has to care rather than a silent fallthrough to "treat
 *   it as a day".
 *
 * ## Why TC39 `Temporal` underneath
 *
 * The distinction above used to be a convention this module hand-enforced, on
 * top of a `Date` that models all three kinds of time as one. `Temporal` makes
 * it a property of the type instead:
 *
 * | This module | TC39 type |
 * |---|---|
 * | calendar date | `Temporal.PlainDate` -- no zone, and no way to acquire one implicitly |
 * | instant | `Temporal.Instant` -- a point on the timeline, no calendar day of its own |
 * | an instant read in a zone | `Temporal.ZonedDateTime` -- the only type that has both |
 *
 * A `PlainDate` cannot be asked what time it is, cannot be compared against an
 * `Instant`, and cannot be rendered "in a timezone", so the conversion this
 * whole change exists to prevent stops being discouraged and becomes
 * unrepresentable. Day arithmetic (`subtract({ days: 1 })`, `dayOfWeek`) comes
 * with it, which is what replaced the epoch-day routines this file used to
 * carry for exactly that reason.
 *
 * `Temporal` is ES2026 and ships in Chrome/Edge 144+ and Firefox 139+, but not
 * in Safari stable, so it arrives via `temporal-polyfill` (~20kB gzipped). It
 * is imported as a ponyfill rather than installed on `globalThis`: nothing else
 * in the bundle is entitled to assume the global exists, and patching it would
 * make the browser-native implementation and the polyfilled one silently
 * interchangeable at runtime.
 */
import { Temporal as TC39Temporal } from "temporal-polyfill";

const { Instant, PlainDate, PlainDateTime, PlainTime } = TC39Temporal;

/**
 * A temporal value as the API states it.
 *
 * The wire contract, identical on every endpoint that adopts it:
 *
 * ```json
 * { "granularity": "date",    "day": "2026-07-30" }
 * { "granularity": "instant", "at": "2026-07-30T14:37:00Z", "zone": "Pacific/Honolulu" }
 * ```
 *
 * `granularity` is always present and is the discriminator. `day` is a bare
 * `YYYY-MM-DD` and appears only on the date arm, which carries no zone because
 * a date has none. On the instant arm `at` is offset-bearing ISO 8601 and
 * `zone` is an IANA name naming the zone the value should be *rendered* in --
 * not a second encoding of the offset.
 */
export type Temporal =
  | { readonly granularity: "date"; readonly day: string }
  | {
      readonly granularity: "instant";
      readonly at: string;
      readonly zone: string;
    };

/**
 * How a day is spelled once it has been decided.
 *
 * - `iso` — `2026-07-30`. What an `<input type="date">` wants, and what a
 *   dense grid cell shows.
 * - `medium` — `Jul 30, 2026`. The app's prose date.
 * - `numeric` — `7/30/2026`.
 * - `monthDay` — `Jul 30`. A chart axis, where the year is already stated by
 *   the range the reader picked and repeating it on every tick costs width the
 *   axis does not have.
 * - `weekdayMedium` — `Thu, Jul 30, 2026`. A day heading over a list of things
 *   that happened on it, where the weekday is what a reader actually orients
 *   by.
 * - `weekdayMonthDay` — `Thu, Jul 30`. The same heading for a day in the
 *   current year, where the year is the one thing the reader already knows.
 * - `weekdayName` — `Thursday`. A day within the week ahead, named in prose.
 * - `monthYear` — `Jul 2026`. A column heading over a value whose period is a
 *   month, where the day within it is noise.
 * - `monthNameYear` — `July 2026`. A month named as a page's subject, in a
 *   heading's meta line, where there is room for the whole word.
 * - `isoMonth` — `2026-07`. The sortable spelling of the same thing, for a
 *   spreadsheet column or a grouping key.
 * - `monthAbbrev` — `JUL`, and `dayOfMonth` — `30`. The two halves of a
 *   calendar-page badge, which stacks them. They are formats rather than
 *   something a caller splits back out of `monthDay`, because splitting a
 *   rendered string is how a renderer ends up re-deriving structure the
 *   formatter already had.
 *
 * The month names, the weekday names and the `M/D/YYYY` ordering are US
 * English, which every date in the app already hardcodes. They are spelled out
 * here rather than left to the viewer's locale so that a calendar date can be
 * built from its own fields without ever being localised into a zone.
 */
export type TemporalFormat =
  | "iso"
  | "medium"
  | "numeric"
  | "monthDay"
  | "weekdayMedium"
  | "weekdayMonthDay"
  | "weekdayName"
  | "monthYear"
  | "monthNameYear"
  | "isoMonth"
  | "monthAbbrev"
  | "dayOfMonth";

/**
 * The date-scalar unit that shows an instant's time of day as well as its day
 * (#14483). The backend sets it on Uploaded At; the attribute editor offers it
 * nowhere, since an organization's own date attributes hold days.
 */
export const DATETIME_UNIT = "datetime";

/** Where a viewer is assumed to be when nothing better is known. */
export const DEFAULT_TIME_ZONE = "America/New_York";

const CALENDAR_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The day a value states, where the caller has already established that the
 * value *is* a day. Anything after it is a fabricated clock, not information.
 */
const DECLARED_CALENDAR_DAY = /^(\d{4}-\d{2}-\d{2})(?:[T ]|$)/;

/** An ISO 8601 datetime that names no offset, so it names no instant. */
const NAIVE_DATETIME = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

/** `Temporal.PlainDate.dayOfWeek` is 1=Monday..7=Sunday, so 7 wraps to 0. */
const WEEKDAY_ABBREVIATIONS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const MONTH_ABBREVIATIONS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Read a bare `YYYY-MM-DD` as a calendar date, or `null` if it is not one.
 *
 * Two guards, both load-bearing:
 *
 * - The regex first, because `PlainDate.from` would happily take the date half
 *   of `2026-07-30T14:37:00Z` and hand back a day. A value that is an instant,
 *   or free text, is not a calendar date and has to come back as `null` so the
 *   caller can leave it alone.
 * - `overflow: "reject"`, because the default is `constrain`, which silently
 *   turns `2018-11-31` into the 30th -- the same quiet repair `new Date` does
 *   in the other direction by rolling forward to December 1.
 */
const parseCalendarDay = (value: string): TC39Temporal.PlainDate | null => {
  if (!CALENDAR_DAY.test(value)) return null;
  try {
    return PlainDate.from(value, { overflow: "reject" });
  } catch {
    return null;
  }
};

/** Read an offset-bearing ISO 8601 string as an instant, or `null`. */
export const parseInstant = (at: string): TC39Temporal.Instant | null => {
  try {
    return Instant.from(at);
  } catch {
    return null;
  }
};

/**
 * The calendar day before this one, or `null` if the input is not a day.
 *
 * Pure day arithmetic, deliberately not "the same clock time 24 hours ago":
 * a day that gains or loses an hour to daylight saving makes that subtraction
 * land on the wrong date, and a calendar date has no clock to subtract from in
 * the first place. `PlainDate` has no clock either, which is why this is now
 * one call rather than a pair of epoch-day conversions.
 */
export const previousCalendarDay = (day: string): string | null => {
  const date = parseCalendarDay(day);
  if (!date) return null;
  return date.subtract({ days: 1 }).toString();
};

/**
 * The calendar day `days` after this one (before it when negative), or `null` if the input is
 * not a day. Day arithmetic, as `previousCalendarDay` is: a range chart's date axis counts its
 * ticks in days from a day it holds, and reads each tick back as a day (#13602).
 */
export const calendarDayAfter = (day: string, days: number): string | null => {
  const date = parseCalendarDay(day);
  if (!date || !Number.isInteger(days)) return null;
  return date.add({ days }).toString();
};

/**
 * Render a bare `YYYY-MM-DD` calendar date.
 *
 * The same string for every viewer on earth, built from the day's own fields.
 * A value that is not a calendar date comes back untouched, so a cell showing
 * free text keeps showing it rather than rendering "Invalid Date".
 */
export const formatCalendarDay = (
  day: string,
  format: TemporalFormat = "iso"
): string => {
  const date = parseCalendarDay(day);
  if (!date) return day;
  const month = MONTH_ABBREVIATIONS[date.month - 1];
  switch (format) {
    case "iso":
      return date.toString();
    case "medium":
      return `${month} ${date.day}, ${date.year}`;
    case "numeric":
      return `${date.month}/${date.day}/${date.year}`;
    case "monthDay":
      return `${month} ${date.day}`;
    case "weekdayMedium":
      return `${WEEKDAY_ABBREVIATIONS[date.dayOfWeek % 7]}, ${month} ${date.day}, ${date.year}`;
    case "weekdayMonthDay":
      return `${WEEKDAY_ABBREVIATIONS[date.dayOfWeek % 7]}, ${month} ${date.day}`;
    case "weekdayName":
      return WEEKDAY_NAMES[date.dayOfWeek % 7];
    case "monthYear":
      return `${month} ${date.year}`;
    case "monthNameYear":
      return `${MONTH_NAMES[date.month - 1]} ${date.year}`;
    case "isoMonth":
      return `${date.year}-${String(date.month).padStart(2, "0")}`;
    case "monthAbbrev":
      return month.toUpperCase();
    case "dayOfMonth":
      return String(date.day);
  }
};

/**
 * The ISO weekday (1 = Monday … 7 = Sunday) of a bare `YYYY-MM-DD` day, or
 * `null` if the input is not a day. The same for every viewer: a calendar
 * day has its weekday by itself, no zone involved.
 */
export const calendarDayWeekday = (day: string): number | null => {
  const date = parseCalendarDay(day);
  return date ? date.dayOfWeek : null;
};

/**
 * A value the *caller* knows is a calendar day, whatever shape it is spelled
 * in, read as the day it states — or `null` if it states none.
 *
 * This is not the string-shape sniffing the module exists to delete. Nothing
 * here decides whether a value is a date or an instant; the caller has already
 * decided, because the field's meaning says so. A fund return's `date` is the
 * calendar day its period ends on, an inception date is the day a fund opened:
 * days, whatever the wire does to them.
 *
 * What the wire does to them is the reason this exists. A backend that parses
 * `2026-03-01` and stamps UTC on it emits `2026-03-01T00:00:00Z`, so the day
 * arrives wearing a fabricated midnight. That clock is not information — it is
 * the artefact of a column that should have been a `date` — and reading it as
 * one is what walks the value back to February for every viewer behind UTC.
 * So the stated day is taken and the fabricated clock is discarded, without
 * ever constructing a `Date`.
 *
 * A value whose day the calendar does not have (`2018-11-31`) states no day and
 * comes back `null`, rather than being quietly nudged to the 30th.
 */
export const calendarDayFrom = (
  value: string | null | undefined
): string | null => {
  if (!value) return null;
  const stated = DECLARED_CALENDAR_DAY.exec(value);
  if (!stated) return null;
  return parseCalendarDay(stated[1])?.toString() ?? null;
};

/** The fields of a calendar day. `month` is 1-indexed, as a calendar is. */
export interface CalendarDayParts {
  readonly year: number;
  readonly month: number;
  readonly day: number;
}

/**
 * The year, month and day of a bare `YYYY-MM-DD`, or `null` if it is not one.
 *
 * The counterpart to `formatCalendarDay` for code that has to *group* by a day's
 * fields rather than print them — a monthly grid keyed by year and month, a
 * quarter derived from a month number. Reading those off a `Date` is where the
 * fields acquire a zone they never had; reading them off the day itself is
 * where they cannot.
 */
export const calendarDayParts = (day: string): CalendarDayParts | null => {
  const date = parseCalendarDay(day);
  if (!date) return null;
  return { year: date.year, month: date.month, day: date.day };
};

/**
 * Order two calendar days: negative if `a` is earlier, positive if later, 0 if
 * they are the same day.
 *
 * A value that names no day sorts after every day, so a malformed entry lands
 * at the end of a list rather than at an arbitrary point in it. Comparing days
 * as instants is the sort half of the same defect as rendering them as instants:
 * it is only ever right by accident, since two days a zone apart can compare
 * equal.
 */
export const compareCalendarDays = (a: string, b: string): number => {
  const left = parseCalendarDay(a);
  const right = parseCalendarDay(b);
  if (!left) return right ? 1 : 0;
  if (!right) return -1;
  return PlainDate.compare(left, right);
};

/**
 * Whole calendar days from `from` to `to` -- negative when `to` is earlier --
 * or `null` if either names no day.
 *
 * Day arithmetic, not elapsed time, and the distinction is the whole reason
 * this exists. Subtracting two epoch values and dividing by 86,400,000 is only
 * right when both ends sit at the same clock time in the same zone: a spring
 * DST transition makes one of those "days" 23 hours long, and the floor turns
 * it into a day that never elapsed. `PlainDate.until` counts the days on the
 * calendar, which is what a reader means by "three days ago" regardless of how
 * many hours those days happened to hold.
 */
export const calendarDaysBetween = (
  from: string,
  to: string
): number | null => {
  const start = parseCalendarDay(from);
  const end = parseCalendarDay(to);
  if (!start || !end) return null;
  return start.until(end, { largestUnit: "day" }).days;
};

/** The first day of the month a calendar day falls in, or `null`. */
export const startOfCalendarMonth = (day: string): string | null => {
  const date = parseCalendarDay(day);
  return date ? date.with({ day: 1 }).toString() : null;
};

/**
 * The last day of the month a calendar day falls in, or `null`.
 *
 * `daysInMonth` comes from the calendar rather than a leap-year rule spelled
 * out here, which is what makes February right without anybody remembering to
 * make it right.
 */
export const endOfCalendarMonth = (day: string): string | null => {
  const date = parseCalendarDay(day);
  return date ? date.with({ day: date.daysInMonth }).toString() : null;
};

/**
 * Whether the runtime's IANA database recognises this zone name.
 *
 * Deliberately `Intl` rather than `Temporal`: `Temporal` accepts a bare offset
 * (`+08:00`) as a time zone, and an offset cannot express daylight saving, so
 * it is exactly the kind of value this check exists to turn away.
 */
export const isValidTimeZone = (
  zone: string | null | undefined
): zone is string => {
  if (!zone) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
};

/**
 * The zone the browser believes it is in, or `null` if it will not say.
 *
 * The browser is the ground truth for a viewer's zone because it follows the
 * person: a stored preference is stale the moment they land somewhere else,
 * which is why there is deliberately no `users.timezone` column to read.
 */
export const browserTimeZone = (): string | null => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

/**
 * The zone a viewer's instants should be rendered in.
 *
 * The browser's zone wins. An organization zone -- from
 * `GET /api/v2/organizations/current/timezone`, which `useViewerTimeZone`
 * supplies -- covers a browser that reports nothing, and Eastern covers an
 * organization that has set none.
 */
export const viewerTimeZone = (
  organizationTimeZone?: string | null,
  browser: string | null = browserTimeZone()
): string => {
  if (isValidTimeZone(browser)) return browser;
  if (isValidTimeZone(organizationTimeZone)) return organizationTimeZone;
  return DEFAULT_TIME_ZONE;
};

/**
 * The calendar day an instant falls on, as seen from `zone`.
 *
 * `ZonedDateTime` is the only one of the three types that has both an instant
 * and a calendar, which is why reading a day off an instant has to pass
 * through it and has to name a zone to do so.
 */
const calendarDayIn = (
  instant: TC39Temporal.Instant,
  zone: string
): TC39Temporal.PlainDate => instant.toZonedDateTimeISO(zone).toPlainDate();

/**
 * Render an instant as the calendar day it falls on in `zone`.
 *
 * `zone` is required and is never defaulted: an instant has no day of its own,
 * so the caller has to say whose day it wants. Pass `viewerTimeZone()` for
 * display and filtering; pass a value's own `zone` when it carries one.
 *
 * A string that names no instant comes back untouched. That includes an ISO
 * datetime carrying no offset, which names no point on the timeline and so is
 * not a valid instant on the wire -- `Date` would have read it as the viewer's
 * local wall clock and shifted the value by their offset.
 */
export const formatInstant = (
  at: string,
  zone: string,
  format: TemporalFormat = "iso"
): string => {
  const instant = parseInstant(at);
  if (!instant) return at;
  return formatCalendarDay(calendarDayIn(instant, zone).toString(), format);
};

/**
 * An instant as a short date and clock in `zone` ("Sep 15, 9:11 AM"), for
 * lists of things that happened — run rows, sends. The viewer's zone is the
 * right one for display (AGENTS.md), so callers pass the
 * pinned viewer zone rather than reading the browser's.
 */
export const formatInstantClock = (at: string, zone: string): string => {
  const instant = parseInstant(at);
  if (!instant) return at;
  return new Intl.DateTimeFormat(undefined, {
    timeZone: zone,
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(instant.epochMilliseconds));
};

/**
 * An instant's clock alone in `zone` ("12:00 AM"), for a line under a date
 * already shown beside it, such as a reset's day over its time.
 */
export const formatInstantTime = (at: string, zone: string): string => {
  const instant = parseInstant(at);
  if (!instant) return at;
  return new Intl.DateTimeFormat(undefined, {
    timeZone: zone,
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(instant.epochMilliseconds));
};

/**
 * An instant as weekday, date and clock in `zone` ("Wed, Oct 7, 9:00 AM"),
 * for things that are going to happen -- a schedule's upcoming runs. The
 * year is added once it is not `now`'s year in that zone, so a series that
 * crosses New Year reads "Thu, Dec 31, 9:00 AM" then "Wed, Mar 31, 2027,
 * 9:00 AM" rather than two dates that could be a year apart either way.
 */
export const formatInstantWeekdayClock = (
  at: string,
  zone: string,
  now: Date = new Date()
): string => {
  const instant = parseInstant(at);
  if (!instant) return at;
  const day = calendarDayIn(instant, zone);
  const thisYear = calendarDayIn(
    Instant.fromEpochMilliseconds(now.getTime()),
    zone
  ).year;
  return new Intl.DateTimeFormat(undefined, {
    timeZone: zone,
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(day.year !== thisYear && { year: "numeric" }),
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(instant.epochMilliseconds));
};

/**
 * An instant as date and clock in `zone` ("Oct 12, 4:14 PM"), with the year
 * once it is not `now`'s year in that zone ("Mar 4, 2025, 4:14 PM"): when
 * something happened, read on its own, like an edit's time.
 */
export const formatInstantDateClock = (
  at: string,
  zone: string,
  now: Date = new Date()
): string => {
  const instant = parseInstant(at);
  if (!instant) return at;
  const day = calendarDayIn(instant, zone);
  const thisYear = calendarDayIn(
    Instant.fromEpochMilliseconds(now.getTime()),
    zone
  ).year;
  return new Intl.DateTimeFormat(undefined, {
    timeZone: zone,
    month: "short",
    day: "numeric",
    ...(day.year !== thisYear && { year: "numeric" }),
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(instant.epochMilliseconds));
};

/**
 * Today's calendar day, in `zone`.
 *
 * The one `Date` left in this module, and it is on the correct side of the
 * line: `now` is an *instant*, which `Date` represents perfectly well -- it is
 * an epoch value. It is taken as a `Date` rather than a `Temporal.Instant`
 * because that is what every caller already has (`new Date()`, or an injected
 * one so a test does not drift with the wall clock).
 *
 * What matters is the direction of travel. An instant becomes a day here, by
 * naming a zone; no day ever becomes a `Date`, which is the conversion `Date`
 * cannot be trusted with and `PlainDate` cannot be asked to perform.
 */
export const todayInZone = (zone: string, now: Date = new Date()): string =>
  calendarDayIn(Instant.fromEpochMilliseconds(now.getTime()), zone).toString();

/**
 * Render a temporal value the way its own granularity says it should be.
 *
 * A date renders verbatim. An instant renders in the zone the value names,
 * which is the zone it wants to be read in -- an interaction shown in the zone
 * the meeting happened in, rather than the reader's.
 *
 * The `never` in the default arm is load-bearing: it is what turns a future
 * `month` or `quarter` granularity into a compile error here instead of a
 * silent fallthrough to "treat it as a day".
 */
export const formatTemporal = (
  value: Temporal,
  format: TemporalFormat = "iso"
): string => {
  switch (value.granularity) {
    case "date":
      return formatCalendarDay(value.day, format);
    case "instant":
      return formatInstant(value.at, value.zone, format);
    default: {
      const unhandled: never = value;
      throw new Error(
        `unhandled temporal granularity: ${JSON.stringify(unhandled)}`
      );
    }
  }
};

/**
 * The calendar day a temporal value falls on *for a reader in `zone`*.
 *
 * The counterpart to `formatTemporal`, and it differs from it in exactly one
 * place: an instant is read in the zone the *reader* is in rather than the zone
 * the value carries. Which of the two is right depends on the question being
 * asked, and both questions are real:
 *
 * - "When did this meeting happen?" is the value's own zone -- a 9am Honolulu
 *   call was at 9am, and re-reading it in New York would misreport the event.
 *   That is `formatTemporal`.
 * - "Which of my days does this land on?" is the reader's zone, and it is the
 *   only sound way to compare a value against *their* today. Bucketing a
 *   UTC-derived day against a local today is the same off-by-one as rendering
 *   a day in the wrong zone, one level up.
 *
 * A date has no zone in either question, so it comes back as the day it states.
 * The `never` arm keeps a future granularity a compile error here rather than a
 * silent fallthrough, for the same reason it does in `formatTemporal`.
 */
export const temporalDayIn = (value: Temporal, zone: string): string => {
  switch (value.granularity) {
    case "date":
      return value.day;
    case "instant":
      return formatInstant(value.at, zone);
    default: {
      const unhandled: never = value;
      throw new Error(
        `unhandled temporal granularity: ${JSON.stringify(unhandled)}`
      );
    }
  }
};

/**
 * What a zone is *called*, as opposed to what it *is*.
 *
 * `America/Los_Angeles` is an engineer's string; a person thinks "Pacific
 * Time". Everything below changes what a reader sees and nothing else -- the
 * value a picker submits, a payload carries, storage keeps and any comparison
 * reads stays the IANA name. That constraint is the point rather than a
 * caution: a friendly name is ambiguous in exactly the way this module exists
 * to stop. `CST` names three different zones, `EST` is Eastern in two
 * hemispheres, and fifteen IANA zones answer to "Eastern Time". A label is a
 * rendering of a zone, in the same sense that `formatInstant` is a rendering of
 * an instant, and is no more storable than one.
 *
 * The shape, and why each third of it is there:
 *
 * ```
 * (UTC-08:00) Pacific Time — Los Angeles
 *  \_______/  \___________/   \_________/
 *   offset      name            city
 * ```
 *
 * - The **offset** is what a reader actually scans, and it is resolved *at an
 *   instant* rather than being a property of the zone: `America/Los_Angeles` is
 *   UTC-08:00 in January and UTC-07:00 in July, and a label that said otherwise
 *   for half the year would be the same class of untruth as a wall clock with
 *   no zone beside it.
 * - The **name** is the *generic* one -- "Pacific Time", not "Pacific Standard
 *   Time". A picker names a zone, not a moment: "Standard" is wrong for half
 *   the year and, worse, would sit next to a UTC-07:00 offset saying the
 *   opposite. `shortTimeZoneName` makes the other choice, and correctly so,
 *   because it labels a *time* -- there the moment is known and `EDT` is a fact
 *   about it.
 * - The **city** disambiguates, because a name alone does not.
 */
export interface TimeZoneLabel {
  /** The IANA name. The value, always -- never the thing a reader is shown. */
  readonly zone: string;
  /** Minutes east of UTC at the instant this was resolved. Sorts the list. */
  readonly offsetMinutes: number;
  /** `UTC-08:00`. */
  readonly offsetLabel: string;
  /** `Pacific Time`, or `""` when the runtime names the zone only by offset. */
  readonly name: string;
  /** `Los Angeles`, or `""` for a zone name with no region segment. */
  readonly city: string;
  /** `Pacific Time — Los Angeles`. The zone, in prose. */
  readonly displayName: string;
  /** `(UTC-08:00) Pacific Time — Los Angeles`. The zone, in a picker. */
  readonly label: string;
}

/**
 * A "name" that is really just an offset -- what the runtime falls back to for
 * a zone its CLDR data has no words for, e.g. `GMT+05:45` or `GMT+9`.
 *
 * Deliberately not matching a bare `GMT`, which is a name a reader knows and
 * the correct answer for the sixteen zones that sit on it. The `+` is what
 * turns it back into an offset wearing a name's clothes.
 */
const OFFSET_SHAPED_ZONE_NAME = /^(?:GMT|UTC)[+-]/;

/** One `Intl` naming of a zone, or `""` if the runtime declines to give one. */
const intlZoneName = (
  zone: string,
  at: Date,
  style: "short" | "shortGeneric" | "long" | "longGeneric"
): string => {
  try {
    return (
      new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: style })
        .formatToParts(at)
        .find((part) => part.type === "timeZoneName")?.value ?? ""
    );
  } catch {
    // `longGeneric` is Intl.DateTimeFormat V3; an older engine throws on it.
    return "";
  }
};

/**
 * Everything a reader needs to be told about a zone, or `null` if the runtime
 * does not recognise it.
 *
 * `null` rather than a fabricated label, so a caller decides what to do with a
 * name its tz database has never heard of -- a picker drops it, a display falls
 * back to the raw name. Guessing on its behalf is how a stale stored zone would
 * come to look like a real one.
 */
export const describeTimeZone = (
  zone: string,
  now: Date = new Date()
): TimeZoneLabel | null => {
  if (!isValidTimeZone(zone)) return null;
  let offsetMinutes: number;
  let offsetLabel: string;
  try {
    const zoned = Instant.fromEpochMilliseconds(
      now.getTime()
    ).toZonedDateTimeISO(zone);
    offsetMinutes = Math.round(zoned.offsetNanoseconds / 60_000_000_000);
    offsetLabel = `UTC${zoned.offset}`;
  } catch {
    return null;
  }

  const generic = intlZoneName(zone, now, "longGeneric");
  const spelled =
    generic && !OFFSET_SHAPED_ZONE_NAME.test(generic)
      ? generic
      : intlZoneName(zone, now, "long");
  const name = OFFSET_SHAPED_ZONE_NAME.test(spelled) ? "" : spelled;

  const segments = zone.split("/");
  const city =
    segments.length > 1 ? segments[segments.length - 1].replace(/_/g, " ") : "";

  // A zone the runtime can neither name nor place still has to read as
  // something, and its own name is the only honest answer left.
  const displayName = [name, city].filter(Boolean).join(" — ") || zone;
  return {
    zone,
    offsetMinutes,
    offsetLabel,
    name,
    city,
    displayName,
    label: `(${offsetLabel}) ${displayName}`,
  };
};

/**
 * How a zone reads in a picker: `(UTC-08:00) Pacific Time — Los Angeles`.
 *
 * A zone the runtime does not recognise comes back unchanged rather than blank,
 * because a stored value can outlive the tz database entry that named it and a
 * row showing nothing is worse than a row showing the raw name.
 */
export const timeZoneLabel = (zone: string, now: Date = new Date()): string =>
  describeTimeZone(zone, now)?.label ?? zone;

/**
 * How a zone reads in a sentence: `Pacific Time — Los Angeles`.
 *
 * The same name without the offset, for prose that is already saying what time
 * it is -- "Currently 2:32 PM in Eastern Time — New York" -- where an offset
 * would be a third number in a line that has enough.
 */
export const timeZoneDisplayName = (
  zone: string,
  now: Date = new Date()
): string => describeTimeZone(zone, now)?.displayName ?? zone;

/**
 * The zones this app will *accept*, from the browser's own tz database rather
 * than a hardcoded list so it never drifts from reality.
 *
 * `Etc/*` is filtered out to match what the API accepts: those are fixed-offset
 * aliases that cannot express daylight saving, and the POSIX sign convention
 * inverts them, so `Etc/GMT+8` means UTC-8.
 *
 * This is the set a stored value is checked against, and it is deliberately
 * *wider* than the list the pickers offer (`timeZoneOptions`): a value we
 * already hold has to stay representable even when the picker has stopped
 * offering that particular name for its zone. Narrowing this to the offered
 * rows would silently move a viewer pinned to `America/Detroit` back to
 * following their browser, and would blank the organization picker for an
 * organization already stored under one.
 */
export const SELECTABLE_TIME_ZONES: readonly string[] =
  typeof Intl.supportedValuesOf === "function"
    ? Intl.supportedValuesOf("timeZone").filter(
        (zone) => !zone.startsWith("Etc/")
      )
    : [];

/**
 * The name to keep when several IANA names describe the same zone.
 *
 * Fifteen entries in the tz database are US Eastern, thirty-three are Central
 * European. They are the same choice offered over and over, so the list keeps
 * one of each -- and *which* one it keeps is a judgement no runtime API will
 * make for us. Every mechanical rule picks a name nobody recognises: shortest
 * and alphabetically-first both prefer `America/Detroit` to
 * `America/New_York`, and tz-database canonicalisation has no opinion at all,
 * since Detroit and New York are separate primary zones rather than aliases.
 *
 * So this is the judgement, written down: the identifier that is idiomatic for
 * the zone -- in practice its largest city, which is also what the tz
 * database's own country aliases point at where it has one (`US/Eastern` ->
 * `America/New_York`, `ROK` -> `Asia/Seoul`). It matters because this name is
 * what gets *stored*, read back in an export and handed to the API.
 *
 * Only zones with a twin need an entry; anything else is the only name for
 * itself and is kept regardless. A group whose members are all absent from
 * this list -- a zone the database splits or merges after this was written --
 * falls back to the alphabetically first name, which is arbitrary but stable,
 * and is a signal that this list wants a line adding rather than a reason to
 * drop a zone.
 */
export const PRINCIPAL_TIME_ZONES: readonly string[] = [
  "Africa/Abidjan",
  "Africa/Algiers",
  "Africa/Johannesburg",
  "Africa/Khartoum",
  "Africa/Lagos",
  "Africa/Nairobi",
  "Africa/Tripoli",
  "America/Anchorage",
  "America/Buenos_Aires",
  "America/Chicago",
  "America/Denver",
  "America/Godthab",
  "America/Halifax",
  "America/Los_Angeles",
  "America/Manaus",
  "America/Mazatlan",
  "America/Mexico_City",
  "America/New_York",
  "America/Panama",
  "America/Phoenix",
  "America/Puerto_Rico",
  "America/Rio_Branco",
  "America/Sao_Paulo",
  "America/Whitehorse",
  "Asia/Almaty",
  "Asia/Bangkok",
  "Asia/Calcutta",
  "Asia/Dubai",
  "Asia/Jakarta",
  "Asia/Kamchatka",
  "Asia/Krasnoyarsk",
  "Asia/Kuala_Lumpur",
  "Asia/Magadan",
  "Asia/Riyadh",
  "Asia/Seoul",
  "Asia/Shanghai",
  "Asia/Tashkent",
  "Asia/Vladivostok",
  "Asia/Yakutsk",
  "Australia/Adelaide",
  "Australia/Brisbane",
  "Australia/Perth",
  "Australia/Sydney",
  "Europe/Athens",
  "Europe/Lisbon",
  "Europe/Moscow",
  "Europe/Paris",
  "Europe/Samara",
  "Pacific/Auckland",
  "Pacific/Guam",
  "Pacific/Majuro",
  "Pacific/Pago_Pago",
];

/** One row of a picker: a zone, named once, with the names it stands for. */
export interface TimeZoneOption extends TimeZoneLabel {
  /**
   * Every IANA name this row describes, `zone` first.
   *
   * A row is what a reader picks; these are what they might have gone looking
   * for. Keeping them means condensing the list takes a name off the screen
   * without taking it out of the search box -- someone who knows their zone as
   * `America/Toronto` can still type it. They are never stored: what a picker
   * submits is `zone`.
   */
  readonly members: readonly string[];
}

/**
 * What a zone's clocks do over the coming year, as a comparable string.
 *
 * Two names describe the same zone only if they agree here as well as reading
 * the same. Reading the same is not enough on its own: `Africa/Cairo` and
 * `Europe/Athens` are both "Eastern European Time" and both UTC+02:00 today,
 * but they change over to summer time on different dates, so treating one as
 * the other would store a zone whose clocks are wrong for weeks of the year.
 * That is a worse defect than the repetition this condensing removes.
 *
 * Monthly samples over a year rather than the transition dates themselves,
 * which no browser API exposes. The accepted limit is a pair of zones whose
 * offsets differ only *between* two samples; a difference that short-lived
 * cannot be seen from here and is not worth a dependency on a tz-rule parser.
 */
const offsetSignature = (zone: string, now: Date): string => {
  const format = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    timeZoneName: "longOffset",
  });
  const offsets: string[] = [];
  for (let month = 0; month < 13; month += 1) {
    const at = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + month, 15)
    );
    offsets.push(
      format.formatToParts(at).find((part) => part.type === "timeZoneName")
        ?.value ?? ""
    );
  }
  return offsets.join(",");
};

/**
 * The zones a picker offers: one row per zone, labelled and in reading order.
 *
 * Two things happen here, and they are separate decisions.
 *
 * **One row per zone, not one per IANA name.** Built straight from the tz
 * database the list runs to four hundred rows, of which fifteen are US Eastern
 * and thirty-three are Central European -- the same choice offered again and
 * again, with nothing to choose between the rows. Names are condensed when a
 * reader would call them the same thing *and* their clocks agree all year (see
 * `offsetSignature`); the one kept is the one a person recognises (see
 * `PRINCIPAL_TIME_ZONES`), and the rest stay on the row as `members` so they
 * remain findable. A zone no other name describes is untouched.
 *
 * **A row is named after its zone rather than a city.** `(UTC-04:00) Eastern
 * Time` -- because a row standing for fifteen names would misrepresent itself
 * by picking one of their cities to wear. The city comes back where it is doing
 * work: for a zone the runtime can only name by offset, and for the rare pair
 * that reads alike but keeps different clocks, where it is the only thing
 * telling the two rows apart. What the row carries is still the IANA name.
 *
 * Ordered by current offset and then by that label, which is the ordering a
 * person choosing a zone is reasoning in. Alphabetical-by-IANA -- what all
 * three pickers did -- sorts by the continent a name happens to start with, so
 * the four zones a US reader is choosing between land 150 rows apart under
 * `America/`, interleaved with every other city on two continents. Because the
 * order follows the *current* offset it moves with daylight saving, which is
 * correct for the same reason the offset in the label is: the list a reader
 * sees should describe the world they are in today.
 *
 * A zone the runtime cannot describe is dropped rather than listed bare -- it
 * came from the tz database, so failing to describe it means the runtime cannot
 * render times in it either.
 */
export const timeZoneOptions = ({
  zones = SELECTABLE_TIME_ZONES,
  now = new Date(),
}: {
  zones?: readonly string[];
  now?: Date;
} = {}): readonly TimeZoneOption[] => {
  const groups = new Map<string, TimeZoneLabel[]>();
  for (const zone of zones) {
    const described = describeTimeZone(zone, now);
    if (!described) continue;
    const key = `${described.offsetLabel} ${described.name} ${offsetSignature(zone, now)}`;
    const group = groups.get(key);
    if (group) group.push(described);
    else groups.set(key, [described]);
  }

  const principal = new Set(PRINCIPAL_TIME_ZONES);
  const rows = [...groups.values()].map((group) => {
    const kept =
      group.find((option) => principal.has(option.zone)) ??
      group.reduce((first, option) =>
        first.zone <= option.zone ? first : option
      );
    return {
      kept,
      members: [
        kept.zone,
        ...group.map((o) => o.zone).filter((z) => z !== kept.zone),
      ],
      zoneLabel: kept.name ? `(${kept.offsetLabel}) ${kept.name}` : kept.label,
    };
  });

  // A label two rows share names neither of them, so both fall back to the
  // city-bearing one. Rare -- it takes two zones that read alike and keep
  // different clocks -- and left to construction rather than to luck, because
  // "no two rows say the same thing" is the property the condensing is for.
  const labelCounts = new Map<string, number>();
  for (const row of rows)
    labelCounts.set(row.zoneLabel, (labelCounts.get(row.zoneLabel) ?? 0) + 1);

  return rows
    .map(({ kept, members, zoneLabel }) => ({
      ...kept,
      label: (labelCounts.get(zoneLabel) ?? 0) > 1 ? kept.label : zoneLabel,
      members,
    }))
    .sort(
      (a, b) =>
        a.offsetMinutes - b.offsetMinutes ||
        a.label.localeCompare(b.label) ||
        a.zone.localeCompare(b.zone)
    );
};

/**
 * What typing in a zone picker's search box is matched against.
 *
 * The label a reader sees, plus every IANA name the row stands for, spelled
 * both as it is written and as it is read. Naming zones in words takes
 * `America/Los_Angeles` off the screen; it should not take it out of the search
 * box, and condensing the list should not take `America/Toronto` out of it
 * either. `Los_Angeles` is included as `Los Angeles` as well, because nobody
 * types the underscore.
 */
export const timeZoneSearchText = (
  option: Pick<TimeZoneOption, "label" | "members">
): string =>
  [
    option.label,
    ...option.members,
    ...option.members.map((zone) => zone.replace(/_/g, " ")),
  ].join(" ");

/**
 * Render the scalar of a `date`-typed cell, or `null` when it names no day.
 *
 * The single rule every reader of an RMS date cell applies -- the grid cell,
 * the clipboard, the client-side export -- so a value can never copy out as a
 * different day than the one on screen. A value carrying the wire contract
 * renders under its own granularity; a column that has not adopted it yet
 * still ships a bare day, which renders verbatim and never becomes an instant.
 *
 * `unit` is the column's `scalar_unit`. Under `datetime` (Uploaded At, #14483)
 * an instant also shows its clock and its zone; a day is still a bare day.
 */
export const formatDateCellScalar = (
  value: unknown,
  format: TemporalFormat = "iso",
  unit?: string | null
): string | null => {
  if (isTemporal(value))
    return unit === DATETIME_UNIT
      ? formatTemporalWithTime(value, format)
      : formatTemporal(value, format);
  if (typeof value === "string" && value)
    return formatCalendarDay(value, format);
  return null;
};

/** Whether a payload field has adopted the wire contract. */
export const isTemporal = (value: unknown): value is Temporal => {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  if (candidate.granularity === "date")
    return typeof candidate.day === "string";
  if (candidate.granularity === "instant")
    return (
      typeof candidate.at === "string" && typeof candidate.zone === "string"
    );
  return false;
};

/**
 * TODO(#11055): delete once every payload carries the wire contract.
 *
 * The one remaining place in the app that infers a temporal's kind from the
 * shape of a string. Two callers are left. One is the file-tree row date
 * (`hooks/useFileTreeV2/rendering.tsx`), where one field holds a document's
 * stated day for some rows and its upload instant for others and the payload
 * says nothing about which. The other is the elapsed-unit grid cell
 * (`RMS/Dashboard/Components/dateScalar.ts`), whose column ships in the wire
 * contract but which still has to read a row cached by an older build. It is a
 * single function rather than six regexes so that every consumer gets the
 * *same* answer while that lasts, and so that deleting it is one edit.
 *
 * `instantZone` is the zone an instant should be rendered in -- the caller's
 * decision, exactly as in `formatInstant`.
 *
 * A datetime naming no offset is read as UTC. Such a value is not a valid
 * temporal on the wire and should not reach here; reading it as UTC matches
 * where the backend's `timestamptz` columns actually sit, whereas letting it
 * be read as the viewer's local wall clock would shift the instant itself by
 * the viewer's offset.
 */
export const temporalFromLegacyString = (
  value: string,
  instantZone: string
): Temporal | null => {
  if (!value) return null;
  if (CALENDAR_DAY.test(value))
    return parseCalendarDay(value) ? { granularity: "date", day: value } : null;
  const at = NAIVE_DATETIME.test(value) ? `${value}Z` : value;
  if (!parseInstant(at)) return null;
  return { granularity: "instant", at, zone: instantZone };
};

/**
 * The calendar day a stored filter bound names, or `null` if it names none.
 *
 * A filter bound is a day the user picked, and a day carries no zone, so the
 * clause stores it as a bare `YYYY-MM-DD` and this hands it straight back.
 * What that day *means* -- an invariant bucket on a date column, the reader's
 * own day on an instant column -- is the column's decision and is made where
 * the column's kind is declared. Nothing here guesses at it.
 *
 * TODO(#11055): the instant arm is a one-time migration of *stored* state and
 * comes out once no saved view or bookmarked URL still holds a pre-#11055
 * bound. Until then a clause saved by the old picker holds local midnight (or
 * local end-of-day) frozen as a UTC instant, and the only way back to the day
 * its author picked is to read it in a zone. `viewerZone` is the reader's,
 * which recovers that day exactly for the person who set the filter and
 * reproduces -- rather than deepens -- the cross-zone drift that made a
 * bound-as-instant the wrong thing to store.
 */
export const filterBoundDay = (
  value: unknown,
  viewerZone: string
): string | null => {
  if (typeof value !== "string" || value === "") return null;
  if (parseCalendarDay(value)) return value;
  const temporal = temporalFromLegacyString(value, viewerZone);
  return temporal ? formatTemporal(temporal) : null;
};

/**
 * TODO(#11055): delete alongside `temporalFromLegacyString`.
 *
 * Render a string that is either a calendar date or an instant, returning
 * `null` when it is neither so the call site can choose its own empty marker.
 */
export const formatLegacyDateString = (
  value: string,
  instantZone: string,
  format: TemporalFormat = "iso"
): string | null => {
  const temporal = temporalFromLegacyString(value, instantZone);
  return temporal ? formatTemporal(temporal, format) : null;
};

/**
 * A wall clock read off an instant: the day and the time of day a particular
 * zone's clocks showed at that moment.
 */
export interface ZonedWallClock {
  readonly day: string;
  readonly time: string;
}

const WALL_CLOCK_TIME = /^([01]\d|2[0-3]):([0-5]\d)$/;

const pad2 = (value: number): string => String(value).padStart(2, "0");

/**
 * The clock `zone` showed at `at`, as a `YYYY-MM-DD` day and an `HH:mm` time.
 *
 * The read half of editing an instant. An editor has to show a wall clock,
 * because that is the only thing a person can type, but *whose* clock is the
 * whole question -- and for an interaction the answer is the value's own zone,
 * never the reader's. Splitting that read out means the editor never has to
 * touch a `Date`, whose only wall clock is the host machine's.
 *
 * `null` when the value names no instant, so a caller shows an empty control
 * rather than a clock it invented.
 */
export const zonedWallClock = (
  at: string,
  zone: string
): ZonedWallClock | null => {
  const instant = parseInstant(at);
  if (!instant || !isValidTimeZone(zone)) return null;
  const zoned = instant.toZonedDateTimeISO(zone);
  return {
    day: zoned.toPlainDate().toString(),
    time: `${pad2(zoned.hour)}:${pad2(zoned.minute)}`,
  };
};

/** The second on `zone`'s clock at the instant `at`; null when either is not valid. */
export const zonedSecond = (at: string, zone: string): number | null => {
  const instant = parseInstant(at);
  if (!instant || !isValidTimeZone(zone)) return null;
  return instant.toZonedDateTimeISO(zone).second;
};

/**
 * The instant at which `zone`'s clocks show `day` at `time`.
 *
 * The write half, and the exact inverse of `zonedWallClock`: an editor hands
 * back the clock it displayed and gets back the moment it names *in the value's
 * own zone*. Re-reading that clock in the viewer's zone instead is the defect --
 * it silently moves a Hawaii meeting by five hours because a New Yorker opened
 * it.
 *
 * `null` when either half is missing or malformed, or when the day is one the
 * calendar does not have, so a caller can decline the save rather than store a
 * moment nobody chose. A clock that does not exist on that day (the hour
 * daylight saving skips) resolves forward via `Temporal`'s `compatible`
 * default, which is what every calendar application does.
 */
export const instantFromWallClock = (
  day: string,
  time: string,
  zone: string
): string | null => {
  const date = parseCalendarDay(day);
  if (!date || !WALL_CLOCK_TIME.test(time) || !isValidTimeZone(zone))
    return null;
  return PlainDateTime.from({
    year: date.year,
    month: date.month,
    day: date.day,
    hour: Number(time.slice(0, 2)),
    minute: Number(time.slice(3, 5)),
  })
    .toZonedDateTime(zone)
    .toInstant()
    .toString();
};

/**
 * What to call `zone` beside a time, at `at`. The shortest name a reader will
 * recognise -- `EST`, `HST`, `GMT`, `Japan Time`, `India Time`.
 *
 * A wall clock with no zone beside it is not a time -- it is a number that
 * happens to look like one, and this app has shipped "20:00" for a meeting the
 * grid called 15:00 because nothing said which clock either was.
 *
 * This is a different problem from the picker label `describeTimeZone` builds,
 * and it gets a different answer. A picker row has a line to itself and one job:
 * make four hundred zones tellable apart, which needs the offset, the name and
 * the city. This sits *inline in a table cell*, after a date and a clock, and
 * has one job too: say which clock that was, in as few characters as will still
 * be recognised.
 *
 * The ladder, measured against the runtime's own data rather than assumed:
 *
 * 1. **The abbreviation**, when there is a real one -- `EST`, `PST`, `HST`,
 *    `AKST`, `GMT`. Unbeatable inline, and resolved *at the instant* because it
 *    is not a property of the zone alone: `America/New_York` is EST in January
 *    and EDT in July.
 * 2. **The short generic name** otherwise -- `Japan Time`, `India Time`,
 *    `North Korea Time`. This is the arm that matters most, because only 95 of
 *    the 418 offerable zones reach step 1: essentially North America and the UK.
 *    For the other three quarters of the world `Intl` answers `GMT+9` or
 *    `GMT+5:30`, which is accurate and reads like a fault -- a second number
 *    where a name belongs, next to a clock that is already a number. The short
 *    generic name covers 402 of the 418 and runs 12 characters at the median.
 * 3. **The long generic name**, then the offset spelled as `UTC+09:00`, for the
 *    remainder. The offset arm should be unreachable for any zone the pickers
 *    offer; it exists so a value that outlived its tz database entry still gets
 *    labelled rather than leaving the clock bare.
 *
 * Steps 2 and 3 lose the standard-versus-daylight distinction that step 1
 * keeps, which is a real inconsistency and the right trade: `Australian Eastern
 * Daylight Time` is 32 characters in a cell that has already spent its width on
 * a date and a clock, and the zones that reach step 2 mostly have no daylight
 * saving to distinguish. The accepted cost is measured rather than hoped for --
 * the longest name any zone yields is 43 characters
 * (`Atlantic/South_Georgia`), and no interaction is going to carry it.
 */
export const shortTimeZoneName = (at: string, zone: string): string => {
  const instant = parseInstant(at);
  if (!instant || !isValidTimeZone(zone)) return "";
  const on = new Date(instant.epochMilliseconds);

  // Bare `GMT` is a name a reader knows; `GMT+9` is the offset wearing one.
  const abbreviation = intlZoneName(zone, on, "short");
  if (abbreviation && !OFFSET_SHAPED_ZONE_NAME.test(abbreviation))
    return abbreviation;

  for (const style of ["shortGeneric", "longGeneric"] as const) {
    const named = intlZoneName(zone, on, style);
    if (named && !OFFSET_SHAPED_ZONE_NAME.test(named)) return named;
  }

  try {
    return `UTC${instant.toZonedDateTimeISO(zone).offset}`;
  } catch {
    return "";
  }
};

/**
 * Render a temporal value including its time of day, where it has one.
 *
 * The counterpart to `formatTemporal`, which answers "which day is this?". This
 * answers "when was this?", and the difference is the whole point of the
 * granularity tag:
 *
 * - An **instant** renders as its day, its wall clock and its zone's name, all
 *   read in the zone the value carries. A meeting's local time is part of what
 *   the meeting *was*, unlike an upload's, which is why this is the one place
 *   the reader's own zone is deliberately not consulted.
 * - A **date** renders as a bare day. It has no time and no zone, so printing
 *   either would be printing something nobody stated.
 *
 * The `never` arm is load-bearing for the same reason it is in `formatTemporal`:
 * a `month` granularity added later must fail to compile here rather than
 * quietly render as a day.
 */
export const formatTemporalWithTime = (
  value: Temporal,
  format: TemporalFormat = "numeric"
): string => {
  switch (value.granularity) {
    case "date":
      return formatCalendarDay(value.day, format);
    case "instant": {
      const clock = zonedWallClock(value.at, value.zone);
      const day = formatInstant(value.at, value.zone, format);
      if (!clock) return day;
      const zoneName = shortTimeZoneName(value.at, value.zone);
      return zoneName
        ? `${day} ${clock.time} ${zoneName}`
        : `${day} ${clock.time}`;
    }
    default: {
      const unhandled: never = value;
      throw new Error(
        `unhandled temporal granularity: ${JSON.stringify(unhandled)}`
      );
    }
  }
};
